interface LocalFn {
    <T extends object>(
        key: string,
        defaultValue: T,
        process?: (value: T, defaultValue: T) => T,
    ): {
        get: () => T;
        set: (value: T) => void;
        clear: () => void;
    };
    <T extends object>(
        key: string,
        defaultValue: null,
        process?: (value: T, defaultValue: null) => T,
    ): {
        get: () => T | null;
        set: (value: T) => void;
        clear: () => void;
    };
}

const extract = (key: string): string | null => {
    try {
        return localStorage.getItem(key);
    } catch {
        return null;
    }
};

const extractJson = <T extends object>(key: string, defaultValue: T | null) => {
    try {
        return JSON.parse(extract(key) || "") as T;
    } catch {
        return defaultValue;
    }
};

const store = (key: string, value: string): boolean => {
    try {
        localStorage.setItem(key, value);
        return true;
    } catch {
        return false;
    }
};

const remove = (key: string): boolean => {
    try {
        localStorage.removeItem(key);
        return true;
    } catch {
        return false;
    }
};

export const local: LocalFn = <T extends object>(
    key: string,
    defaultValue: T | null,
    process?: (value: T, defaultValue: T | null) => T,
) => {
    let value = extractJson(key, defaultValue);

    if (process && value) {
        value = process(value, defaultValue);
    }

    const set = (newValue: T) => {
        value = newValue;
        store(key, JSON.stringify(value));
    };

    const get = () => value;

    const clear = () => {
        value = defaultValue;
        remove(key);
    };

    return { get, set, clear };
};

export const localBool = (key: string) => {
    let value: boolean = extract(key) === "1";

    const get = () => value;

    const set = (newValue: boolean): boolean => {
        const result = store(key, newValue ? "1" : "0");
        if (result) {
            value = newValue;
        }
        return result;
    };

    const clear = (): boolean => {
        return remove(key);
    };

    return { get, set, clear };
};
